import { Group } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus } from "@tabler/icons-react"
import { OnChangeFn, PaginationState } from "@tanstack/react-table"
import { CertificateCategory } from "@tfg/db"
import { Button, Loader, SearchInput, Select } from "@tfg/ui"
import { useEffect } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import {
  certifcatefiltersValidationType,
  certificateTypeCreateSchema,
  certificateTypeCreateValidationType,
} from "@tfg/types/schemas/admin"
import {
  CertificateTriggerSchema,
  CertificateTriggerType,
} from "@tfg/types/schemas/enum"

import { useFindStandards } from "~/gen/api"
import { FormFieldConfig } from "~/pages/Admin/components/TypesForm"
import { showAxiosError } from "~/utils/showAxiosError"

import { DrawerForm } from "../DrawerForm"

import { CertificatesTable } from "./CertificatesTable"
import { useCreateCertificateTypeMutation } from "./hook"

type Props = {
  certificates: AdminResponses.CertificateTypesWithTotal
  pagination: { pageIndex: number; pageSize: number }
  setPagination: OnChangeFn<PaginationState>
  setPageCount: (pageCount: number) => void
  pageCount: number
  setSearchCertificateType: (searchCertificateType: string) => void
  searchCertificateType: string
  isFetching: boolean
  filterValues: certifcatefiltersValidationType
  setFilterValues: (filterValues: certifcatefiltersValidationType) => void
}

export function CertifcateTypes({
  certificates,
  pagination,
  pageCount,
  setPagination,
  setSearchCertificateType,
  searchCertificateType,
  setPageCount,
  isFetching,
  filterValues,
  setFilterValues,
}: Props) {
  const [drawerOpen, drawerActions] = useDisclosure(false)
  const { mutate: createCertificateType } = useCreateCertificateTypeMutation({
    onSuccess: drawerActions.close,
    onError: (error) => {
      showAxiosError({
        notificationTitle: "Error",
        notificationMessage: "Error updating Certificate Type",
        showNotification: true,
        error: error as Error,
      })
    },
  })
  const { certificateTypes, totalCertificateTypes } = certificates
  useEffect(() => {
    if (totalCertificateTypes === 0) {
      setPageCount(0)
    } else if (totalCertificateTypes > 0) {
      setPageCount(Math.ceil(totalCertificateTypes / pagination.pageSize))
    }
  }, [totalCertificateTypes, pagination.pageSize, setPageCount])
  const { data: standardsList, isPending: isLoading } = useFindStandards()
  const formattedOptions =
    standardsList?.data?.map((option) => ({
      value: option.id.toString(),
      label: option.name,
    })) ?? []

  const fields: FormFieldConfig[] = [
    {
      type: "text",
      name: "name",
      label: "Name",
      placeholder: "Name",
    },
    {
      type: "select",
      name: "category",
      label: "Category",
      placeholder: "Category",
      options: Object.values(CertificateCategory).map((option) => ({
        value: option,
        label: option,
      })),
    },
    {
      type: "select",
      name: "trigger",
      label: "Trigger",
      placeholder: "trigger",
      options: Object.values(CertificateTriggerSchema.enum).map((option) => ({
        value: option,
        label: option,
      })),
    },
    {
      type: "multiselect",
      name: "standard",
      label: "Standards",
      placeholder: "Standards",
      options: formattedOptions,
    },
  ]
  return (
    <>
      <Group justify="space-between" gap="sm" py="sm">
        <Group gap="sm">
          <SearchInput
            size="xs"
            w={200}
            placeholder="Search certificate types"
            inputValue={searchCertificateType}
            delay={0}
            onChange={(value) => setSearchCertificateType(value.toString())}
            styles={{
              input: {
                backgroundColor: "white",
              },
            }}
            rightSection={isFetching && <Loader size={16} />}
          />
          <Select
            data={Object.values(CertificateTriggerSchema.enum)}
            w={200}
            size="xs"
            value={filterValues.trigger}
            searchable
            onChange={(value) =>
              setFilterValues({
                ...filterValues,
                trigger:
                  value && value in CertificateTriggerSchema.enum
                    ? (value as CertificateTriggerType)
                    : undefined,
              })
            }
            placeholder="Trigger"
            styles={{
              input: {
                backgroundColor: "white",
              },
            }}
          />
        </Group>
        <Button onClick={() => drawerActions.open()} icon={<IconPlus />}>
          New Certificate Type
        </Button>
      </Group>
      {drawerOpen ? (
        isLoading ? null : (
          <DrawerForm<certificateTypeCreateValidationType>
            fields={fields}
            initialValues={{
              name: "",
              standard: [],
              category: "TRANSACTION",
              trigger: CertificateTriggerSchema.enum.LAST_SUPPLIER,
              attributes: undefined,
            }}
            drawerActions={drawerActions}
            schema={certificateTypeCreateSchema}
            mutate={createCertificateType}
          />
        )
      ) : null}
      {isLoading ? (
        <Loader />
      ) : (
        <CertificatesTable
          certificateTypes={certificateTypes}
          standardsList={standardsList?.data || []}
          setPagination={setPagination}
          pageCount={pageCount}
          pagination={pagination}
          totalCertificateTypes={totalCertificateTypes}
        />
      )}
    </>
  )
}
